import { Flex, Text, Badge, Box, SimpleGrid, Stack, Button } from "@mantine/core";
import {
  IconBuilding,
  IconMapPin,
  IconTool,
  IconShieldCheck,
  IconBriefcase,
  IconTarget,
  IconUserCheck,
  IconUser,
  IconDownload,
} from "@tabler/icons-react";
import ProductsServicesTab from "./ProductsServicesTab";
import ProfileCollapsibleSection from "@/components/shared/ProfileCollapsibleSection";
import ProfileImagesDocumentsSections from "@/components/shared/ProfileImagesDocumentsSections";
import {
  ANONYMIZED_MASK,
  anonymizedVerificationFieldValue,
} from "@/shared/utils/anonymizedProfileDisplay";
import { formatCompanyProfileTextForDisplay } from "@/shared/utils/capitalizeLeadingCharacter";
import {
  getCollaborationIntentLabel,
  getCollaborationRoleLabel,
  getWhoAmILabel,
  getIndustryLabel,
  getSubIndustryLabel,
  getAdditionalTechInfraLabel,
  regionsData,
} from "../constants/general.const";
interface ProfileTabProps {
  company: any;
  collaboration?: any;
  /** When true, viewer sent the collaboration request (labels use "your" / expected partner wording). */
  isRequester?: boolean;
  /** Logged-in viewer's company collaboration intents (used when isRequester). */
  viewerCollaborationIntent?: string[];
  onDownloadVerificationPdf?: () => void | Promise<void>;
  verificationPdfDownloading?: boolean;
}

export default function ProfileTab({
  company,
  collaboration,
  isRequester = false,
  viewerCollaborationIntent,
  onDownloadVerificationPdf,
  verificationPdfDownloading,
}: ProfileTabProps) {
  const getRegionLabel = (regionId: string): string => {
    const region = regionsData.regions.find((r) => r.id === regionId);
    return region ? region.name : regionId;
  };
  const metadata = company.externalVerificationLogs?.[0]?.metadata;

  const profileCollaborationIntents = isRequester
    ? Array.isArray(viewerCollaborationIntent)
      ? viewerCollaborationIntent
      : []
    : Array.isArray(company.collaborationIntent)
      ? company.collaborationIntent
      : [];
  const hasProfileCollaborationIntents = profileCollaborationIntents.length > 0;

  return (
    <>
      {/* Collaboration Intent Details */}
      {collaboration &&
        (collaboration.requesterRole ||
          collaboration.expectedPartnerRole ||
          hasProfileCollaborationIntents ||
          (typeof collaboration.expressInterestTitle === "string" && collaboration.expressInterestTitle.trim() !== "") ||
          (typeof collaboration.expressInterestSummary === "string" &&
            collaboration.expressInterestSummary.trim() !== "")) && (
          <ProfileCollapsibleSection
            title="Collaboration Intent Details"
            icon={<IconBriefcase size={18} color="#ffc300" stroke={1.5} />}
            defaultOpen
          >
            <Text size="xs" c="slate.5" mb="lg">
              Information provided by the company about their collaboration goals
            </Text>

            <Flex direction="column" gap="md">
              {(typeof collaboration.expressInterestTitle === "string" &&
                collaboration.expressInterestTitle.trim() !== "") ||
                (typeof collaboration.expressInterestSummary === "string" &&
                  collaboration.expressInterestSummary.trim() !== "") ? (
                <Box>
                  {typeof collaboration.expressInterestTitle === "string" &&
                    collaboration.expressInterestTitle.trim() !== "" && (
                      <Box mb="md">
                        <Text size="xs" c="slate.6" tt="uppercase" fw={400} mb="xs">
                          Title
                        </Text>
                        <Text
                          size="sm"
                          fw={600}
                          c="slate.8"
                          style={{ lineHeight: 1.5, wordBreak: "break-word" }}
                        >
                          {collaboration.expressInterestTitle.trim()}
                        </Text>
                      </Box>
                    )}
                  {typeof collaboration.expressInterestSummary === "string" &&
                    collaboration.expressInterestSummary.trim() !== "" && (
                      <Box>
                        <Text size="xs" c="slate.6" tt="uppercase" fw={400} mb="xs">
                          Summary
                        </Text>
                        <Text
                          size="sm"
                          c="slate.7"
                          style={{ lineHeight: 1.55, wordBreak: "break-word", textAlign: "justify" }}
                        >
                          {collaboration.expressInterestSummary.trim()}
                        </Text>
                      </Box>
                    )}
                </Box>
              ) : null}

              {hasProfileCollaborationIntents && (
                <Flex align="center" gap="sm" wrap="wrap">
                  <IconTarget
                    size={16}
                    color="var(--mantine-color-primary-6)"
                    style={{ flexShrink: 0 }}
                  />
                  <Text size="xs" c="slate.6" tt="uppercase" fw={400}>
                    Collaboration type
                  </Text>
                  <Text size="sm" c="slate.7" fw={500}>
                    {profileCollaborationIntents
                      .map((intent: string) => getCollaborationIntentLabel(intent))
                      .join(", ")}
                  </Text>
                </Flex>
              )}

              {(collaboration.requesterRole || collaboration.expectedPartnerRole) && (
                <Flex direction="column" gap="md" w="100%">
                  {collaboration.requesterRole && (
                    <Flex align="center" gap="sm" wrap="wrap">
                      <IconBriefcase
                        size={16}
                        color="var(--mantine-color-primary-6)"
                        style={{ flexShrink: 0 }}
                      />
                      <Text size="xs" c="slate.6" tt="uppercase" fw={400}>
                        {isRequester
                          ? "Your role in this collaboration"
                          : "Their role in this collaboration"}
                      </Text>
                      <Text size="sm" c="slate.7" fw={500}>
                        {getCollaborationRoleLabel(collaboration.requesterRole)}
                      </Text>
                    </Flex>
                  )}

                  {collaboration.expectedPartnerRole && (
                    <Flex align="center" gap="sm" wrap="wrap">
                      <IconUserCheck
                        size={16}
                        color="var(--mantine-color-primary-6)"
                        style={{ flexShrink: 0 }}
                      />
                      <Text size="xs" c="slate.6" tt="uppercase" fw={400}>
                        {isRequester ? "Expected Partner Role" : "What they expect from you"}
                      </Text>
                      <Text size="sm" c="slate.7" fw={500}>
                        {getCollaborationRoleLabel(collaboration.expectedPartnerRole)}
                      </Text>
                    </Flex>
                  )}
                </Flex>
              )}
            </Flex>
          </ProfileCollapsibleSection>
        )}

      {/* Business Overview */}
      <ProfileCollapsibleSection
        title="Business Overview"
        icon={<IconBuilding size={18} color="#ffc300" stroke={1.5} />}
        defaultOpen
      >
        <Flex gap="xl" wrap="wrap">
          <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
            <Text size="md" fw={600} c="slate.8" tt="uppercase" mb="xs">
              COMPANY DESCRIPTION
            </Text>
            <Text size="sm" style={{ lineHeight: 1.5, wordBreak: "break-word", overflow: "hidden", textAlign: "justify" }}>
              {formatCompanyProfileTextForDisplay(company.description)}
            </Text>
          </Flex>

          <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
            <Text size="md" fw={600} c="slate.8" tt="uppercase" mb="xs">
              SUB DESCRIPTION
            </Text>
            <Text size="sm" style={{ lineHeight: 1.5, wordBreak: "break-word" }}>
              {formatCompanyProfileTextForDisplay(company.subDescription)}
            </Text>
          </Flex>

          <Flex direction="row" style={{ flex: "1 1 100%", minWidth: "280px" }}>
            <Text size="sm" c="slate.8" fw={700} mb="xs">
              OPERATIONS
            </Text>
            <Flex gap="xs" wrap="wrap">
              {Array.isArray(company.operations) && company.operations.length > 0 ? (
                company.operations.map((op: string, i: number) => (
                  <Badge key={`${op}-${i}`} variant="light" color="blue" bg="#EFF6FF" c="#1D4ED8">
                    {op}
                  </Badge>
                ))
              ) : (
                <Text size="sm">N/A</Text>
              )}
            </Flex>
          </Flex>

          {/* <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
            <Text size="xs" c="slate.6" tt="uppercase" mb="xs">
              YEARS OF OPERATION
            </Text>
            <Text size="sm">{getYearsOfOperation()}</Text>
          </Flex> */}

          <Flex style={{ flex: "1 1 100%" }} justify="space-between" gap="md" wrap="wrap">
            <Flex style={{ flex: "1 1 48%", minWidth: "280px" }} gap="md" wrap="wrap">
              <Flex direction="column" style={{ flex: "1 1 46%", minWidth: "220px" }}>
                <Text size="sm" c="slate.8" fw={700} mb="xs">
                  INDUSTRY CATEGORY
                </Text>
                <Text size="sm">
                  {Array.isArray(company.industryCategory) && company.industryCategory.length > 0
                    ? company.industryCategory.map((c: string) => getIndustryLabel(c)).join(", ")
                    : "N/A"}
                </Text>
              </Flex>

              <Flex direction="column" style={{ flex: "1 1 46%", minWidth: "220px" }}>
                <Text size="sm" c="slate.8" fw={700} mb="xs">
                  SUB INDUSTRY
                </Text>
                <Text size="sm">
                  {Array.isArray(company.subIndustry) && company.subIndustry.length > 0
                    ? company.subIndustry.map((sub: string) => getSubIndustryLabel(sub)).join(", ")
                    : "N/A"}
                </Text>
              </Flex>
            </Flex>

            <Flex style={{ flex: "1 1 48%", minWidth: "280px" }} gap="md" wrap="wrap">
              <Flex direction="column" style={{ flex: "1 1 46%", minWidth: "220px" }}>
                <Text size="sm" c="slate.8" fw={700} mb="xs">
                  WHO AM I
                </Text>
                <Text size="sm">
                  {Array.isArray(company.whoAmI) && company.whoAmI.length > 0
                    ? company.whoAmI.map((role: string) => getWhoAmILabel(role)).join(", ")
                    : "N/A"}
                </Text>
              </Flex>

              <Flex direction="column" style={{ flex: "1 1 46%", minWidth: "220px" }}>
                <Text size="sm" c="slate.8" fw={700} mb="xs">
                  COLLABORATION INTENT
                </Text>
                <Text size="sm">
                  {Array.isArray(company.collaborationIntent) && company.collaborationIntent.length > 0
                    ? company.collaborationIntent.map((intent: string) => getCollaborationIntentLabel(intent)).join(", ")
                    : "N/A"}
                </Text>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </ProfileCollapsibleSection>

      {/* Products & Services */}
      <ProductsServicesTab company={company} />

      {/* Capabilities & Offerings */}
      <ProfileCollapsibleSection
        title="Capabilities & Offerings"
        icon={<IconTool size={18} color="#ffc300" stroke={1.5} style={{ transform: "scaleX(-1)" }} />}
      >
        <Flex gap="xl" wrap="wrap" mb="lg">
          <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
            <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
              CORE CAPABILITIES
            </Text>
            <Flex gap="xs" wrap="wrap">
              {(company.coreCapabilities || []).map((capability: string, index: number) => (
                <Badge key={index} style={{ backgroundColor: "#04305c", color: "white" }} py={14} px={12} size="md" fw={500}>
                  {capability}
                </Badge>
              ))}
              {(!company.coreCapabilities || company.coreCapabilities.length === 0) && (
                <Text size="sm" c="slate.6">
                  N/A
                </Text>
              )}
            </Flex>
          </Flex>

          {company.secondaryCapabilities && company.secondaryCapabilities.length > 0 && (
            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                SECONDARY CAPABILITIES
              </Text>
              <Flex gap="xs" wrap="wrap">
                {company.secondaryCapabilities.map((capability: string, index: number) => (
                  <Badge key={index} style={{ backgroundColor: "#04305c", color: "white" }} py={14} px={12} size="md" fw={500}>
                    {capability}
                  </Badge>
                ))}
              </Flex>
            </Flex>
          )}
        </Flex>

        {company.keyDifferentiators && (
          <Flex direction="column" mb="lg">
            <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
              KEY DIFFERENTIATORS
            </Text>
            <Text size="sm">{company.keyDifferentiators}</Text>
          </Flex>
        )}

        <Flex direction="column" mb="lg">
          <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
            TECHNOLOGY & INFRASTRUCTURE
          </Text>
          <Flex gap="xs" wrap="wrap">
            {(company.technologies || []).map((tech: string, index: number) => (
              <Badge key={index} style={{ backgroundColor: "#04305c", color: "white" }} py={14} px={12} size="md" fw={500}>
                {tech}
              </Badge>
            ))}
            {(!company.technologies || company.technologies.length === 0) && (
              <Text size="sm" c="slate.6">
                N/A
              </Text>
            )}
          </Flex>
        </Flex>

        {company.additionalTechInfrastructure && company.additionalTechInfrastructure.length > 0 && (
          <Flex direction="column">
            <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
              ADDITIONAL TECHNOLOGY & INFRASTRUCTURE
            </Text>
            <Flex gap="xs" wrap="wrap">
              {company.additionalTechInfrastructure.map((item: string, index: number) => (
                <Badge key={index} style={{ backgroundColor: "#04305c", color: "white" }} py={14} px={12} size="md" fw={500}>
                  {getAdditionalTechInfraLabel(item)}
                </Badge>
              ))}
            </Flex>
          </Flex>
        )}
      </ProfileCollapsibleSection>

      {/* Geography & Markets */}
      <ProfileCollapsibleSection
        title="Geography & Markets"
        icon={<IconMapPin size={18} color="#ffc300" stroke={1.5} />}
      >
        <Flex gap="xl" wrap="wrap">
          <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
            <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
              OPERATING GEOGRAPHIES
            </Text>
            <Flex gap="xs" wrap="wrap">
              {(company.operatingCountries || []).map((country: string, index: number) => (
                <Badge key={index} style={{ backgroundColor: "#04305c", color: "white" }} py={14} px={12} size="md" fw={500}>
                  {country}
                </Badge>
              ))}
              {(!company.operatingCountries || company.operatingCountries.length === 0) && (
                <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                  N/A
                </Text>
              )}
            </Flex>
          </Flex>

          <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
            <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
              PREFERRED COLLABORATION REGIONS
            </Text>
            <Flex gap="xs" wrap="wrap">
              {(company.preferredCollaborationRegions || []).map((region: string, index: number) => (
                <Badge key={index} style={{ backgroundColor: "#04305c", color: "white" }} py={14} px={12} size="md" fw={500}>
                  {getRegionLabel(region)}
                </Badge>
              ))}
              {(!company.preferredCollaborationRegions || company.preferredCollaborationRegions.length === 0) && (
                <Text size="sm" c="slate.6">
                  N/A
                </Text>
              )}
            </Flex>
          </Flex>
        </Flex>
      </ProfileCollapsibleSection>

      {/* Contact Person - masked */}
      <ProfileCollapsibleSection
        title="Contact Person"
        icon={<IconUser size={18} color="#ffc300" stroke={1.5} />}
      >
        <Box
          pt="md"
          pb="md"
          style={{
            borderTop: "1px solid #E5E7EB",
            borderBottom: "1px solid #E5E7EB",
          }}
        >
          <Flex gap="md" wrap="wrap">
            <Flex direction="column" style={{ flex: "1 1 220px", minWidth: "220px" }}>
              <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                NAME
              </Text>
              <Text size="sm">{ANONYMIZED_MASK}</Text>
            </Flex>
            <Flex direction="column" style={{ flex: "1 1 220px", minWidth: "220px" }}>
              <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                PHONE NUMBER
              </Text>
              <Text size="sm">{ANONYMIZED_MASK}</Text>
            </Flex>
            <Flex direction="column" style={{ flex: "1 1 220px", minWidth: "220px" }}>
              <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                DESIGNATION
              </Text>
              <Text size="sm">{ANONYMIZED_MASK}</Text>
            </Flex>
          </Flex>
        </Box>
      </ProfileCollapsibleSection>

      {/* System Verification Details */}
      {metadata && (
        <ProfileCollapsibleSection
          title="System Verification Details"
          icon={<IconShieldCheck size={18} color="#ffc300" stroke={1.5} />}
          headerRight={
            onDownloadVerificationPdf ? (
              <Button
                variant="white"
                bg="#FFC300"
                color="dark"
                size="compact-sm"
                leftSection={<IconDownload size={16} />}
                onClick={() => void onDownloadVerificationPdf()}
                loading={verificationPdfDownloading}
                disabled={verificationPdfDownloading}
                style={{ flexShrink: 0 }}
              >
                Download
              </Button>
            ) : undefined
          }
        >
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing={{ base: "lg", sm: "xl" }} verticalSpacing="lg">
            <Stack gap="xs">
              <Text size="md" c="slate.8" tt="uppercase" fw={600}>
                COMPANY NAME
              </Text>
              <Text size="sm">{anonymizedVerificationFieldValue(metadata.companyName, true)}</Text>
            </Stack>
            <Stack gap="xs">
              <Text size="md" c="slate.8" tt="uppercase" fw={600}>
                COMPANY ADDRESS
              </Text>
              <Text size="sm">{anonymizedVerificationFieldValue(metadata.companyAddress, true)}</Text>
            </Stack>
            <Stack gap="xs">
              <Text size="md" c="slate.8" tt="uppercase" fw={600}>
                DATE OF REGISTRATION OF CERTIFICATE
              </Text>
              <Text size="sm">{anonymizedVerificationFieldValue(metadata.dateOfIncorporation, true)}</Text>
            </Stack>
            <Stack gap="xs">
              <Text size="md" c="slate.8" tt="uppercase" fw={600}>
                REGISTRATION NUMBER
              </Text>
              <Text size="sm">{anonymizedVerificationFieldValue(metadata.registrationNumber, true)}</Text>
            </Stack>
            <Stack gap="xs">
              <Text size="md" c="slate.8" tt="uppercase" fw={600}>
                COMPANY TYPE
              </Text>
              <Text size="sm">{anonymizedVerificationFieldValue(metadata.companyType, true)}</Text>
            </Stack>
            <Stack gap="xs">
              <Text size="md" c="slate.8" tt="uppercase" fw={600}>
                COMPANY STATUS
              </Text>
              <Text size="sm">{anonymizedVerificationFieldValue(metadata.companyStatus, true)}</Text>
            </Stack>
          </SimpleGrid>
        </ProfileCollapsibleSection>
      )}

      <ProfileImagesDocumentsSections
        company={company}
        mode="view"
        anonymousView
        refreshCompany={async () => { }}
      />
    </>
  );
}
